import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { ca } from '../i18n/ca';

/** One thing the reader can do about a refusal, as a button. */
export interface RefusalAction {
  key: string;
  label: string;
  run: () => void;
}

/**
 * A generation refusal, said in a dialog of the page's own (the owner,
 * 2026-10-11: «el missatge de rebuig hauria de ser un popup, no un text mig
 * amagat a la pàgina» — it was a small red paragraph under the title field,
 * below the fold of a long step, read by members waiting to play).
 *
 * The Catalan sentence stands alone; what the platform worked out would work
 * instead is a list of buttons that act; the platform's own technical words
 * stay behind «Detall tècnic», closed, for whoever is asked to read them out.
 * Built by hand like {@link ConfirmDialog}, and for its reasons: the browser's
 * `alert` blocks the renderer and cannot carry a button, and jsdom has no
 * `showModal`. Escape and a click outside close it; focus goes into it and
 * back where it was; Tab stays inside.
 */
export function RefusalDialog({
  text,
  lead,
  detail,
  actions,
  link,
  onClose,
}: {
  /** The sentence, complete on its own. */
  text: string;
  /** What the sentence is about, said before it in bold (a parameter's name), or nothing. */
  lead?: string | null;
  /** The platform's own detail, shown only when the reader opens it. */
  detail?: string | null;
  /** What can be done instead, each a button; the first takes the focus. */
  actions: RefusalAction[];
  /** The step that fixes it, as a link, when the platform named one. */
  link?: ReactNode;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);

  // Into the dialog on opening — the first thing to do when there is one, the
  // closing button otherwise — and back to where the reader was on closing.
  // A layout effect: the focus is inside before the dialog is painted, never
  // a frame later with the page's own button still holding it.
  useLayoutEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>('.refusal-actions button');
    (first ?? closeButton.current)?.focus();
    return () => before?.focus?.();
  }, []);

  // A layout effect as well: Escape and the Tab trap answer from the commit
  // that drew the dialog. As a passive effect they came a task later on a busy
  // machine, and a key pressed in between went to the page under the dialog.
  useLayoutEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panel.current) return;
      const stops = [...panel.current.querySelectorAll<HTMLElement>('button, a[href], summary')];
      if (stops.length === 0) return;
      const first = stops[0];
      const last = stops[stops.length - 1];
      const inside = panel.current.contains(document.activeElement);
      if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault();
        last.focus();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const t = ca.missionUi.refusal;
  return (
    <div
      className="confirm-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="confirm-panel refusal-panel"
        ref={panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="refusal-title"
        aria-describedby="refusal-text"
      >
        <h2 id="refusal-title">{t.title}</h2>
        <p id="refusal-text" className="refusal-text">
          {lead && <strong>{lead}: </strong>}
          {text}
        </p>
        {(actions.length > 0 || link) && (
          <div className="refusal-actions">
            <span className="muted small">{t.whatNow}</span>
            {actions.map((a) => (
              <button type="button" key={a.key} onClick={a.run}>
                {a.label}
              </button>
            ))}
            {link}
          </div>
        )}
        {detail && (
          <details className="refusal-detail">
            <summary>{t.technical}</summary>
            <p className="small">{detail}</p>
          </details>
        )}
        <div className="row end">
          <button type="button" ref={closeButton} className="primary" onClick={onClose}>
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
}
